import React from 'react';
import { Artifact, ARTIFACT_MAIN, UNIT_STYLE_NAME, ATTRIBUTE_NAME, artifactEffectText, QUALITY } from './runeEngine';

/**
 * **아티팩트 — 게임 룬 창 왼쪽의 둥근 칸 둘과, 눌렀을 때의 창.**
 *
 * 속성 아티팩트는 속성 색 테두리에 속성 글자, 유형 아티팩트는 유형 색 테두리에 유형 글자. 비어
 * 있으면 어두운 빈 칸. 창에는 "+15 바람 속성 아티팩트", 등급, 주스탯 크게, 부옵션 넷(게임 문구).
 *
 * 예전에 "유물" 이라고 적혀 있었는데, 이 사이트에서 유물은 룬 별 오른쪽 육각(`RelicDetail`)이라
 * 헷갈렸다 — 게임 표기대로 아티팩트라 부른다.
 */

/**
 * 속성 번호 — 1 물 · 2 불 · 3 바람 · 4 빛 · 5 어둠, 그리고 **98 무속성**(어느 속성 몹에나 낀다 —
 * 실측 2026-10-09, 물·바람·빛 몹에 하나씩 끼여 있었다). 타입형에도 같은 **98 무형**이 있다(실측
 * 10개). 둘 다 몹 속성·유형 표에 없어서 여기서 따로 부른다.
 */
const NEUTRAL = 98;
export const ATTR_COLOR: Record<number, string> = { 1: '#3fa9f5', 2: '#f0562d', 3: '#d9b13a', 4: '#f2e6c9', 5: '#8d5bd6', [NEUTRAL]: '#c9c9c9' };
const STYLE_COLOR: Record<number, string> = { 1: '#e0562d', 2: '#3fa9f5', 3: '#5fbf5a', 4: '#f2c14e', 5: '#999', [NEUTRAL]: '#c9c9c9' };
const ATTR_SHORT: Record<number, string> = { 1: '수', 2: '화', 3: '풍', 4: '광', 5: '암', [NEUTRAL]: '무' };
const STYLE_SHORT: Record<number, string> = { 1: '공', 2: '방', 3: '체', 4: '보', 5: '재', [NEUTRAL]: '무' };

function label(a: Artifact): string {
    if (a.type === 1) return a.attribute === NEUTRAL ? '무속성 아티팩트' : `${ATTRIBUTE_NAME[a.attribute] || '?'} 속성 아티팩트`;
    return a.unitStyle === NEUTRAL ? '무형 아티팩트' : `${UNIT_STYLE_NAME[a.unitStyle] || '?'} 아티팩트`;
}

/**
 * 아티팩트 그림 — 게임 모양(금테 · 등급색 보석 · 흰 문양). SWARFARM 그림을 겹쳐 미리 만들어 둔 것이다
 * (`backend/tools/make-artifact-icons.py`). 속성은 `attr-<속성>-<등급>`, 유형은 `style-<유형>-<등급>`.
 */
export function artifactImg(a: Artifact): string | null {
    const n = a.type === 1 ? a.attribute : a.unitStyle;
    const max = a.type === 1 ? 5 : 4;
    const known = (n >= 1 && n <= max) || n === NEUTRAL;
    if (!known || !(a.rank >= 1 && a.rank <= 5)) return null;
    return `/img/artifacts/${a.type === 1 ? 'attr' : 'style'}-${n}-${a.rank}.webp`;
}

/** 둥근 아티팩트 칸 — 별 왼쪽에 둔다 */
export function ArtifactBadge({ artifact, kind, size, picked, onClick }: {
    artifact: Artifact | null;
    /** 비어 있을 때 무슨 칸인지 — 1 속성 · 2 유형 */
    kind: number;
    size: number;
    picked?: boolean;
    onClick?: () => void;
}) {
    const [broken, setBroken] = React.useState(false);
    const src = artifact && !broken ? artifactImg(artifact) : null;
    if (artifact && src) {
        return (
            <div
                role={onClick ? 'button' : undefined}
                onClick={onClick}
                title={`${label(artifact)} · ${ARTIFACT_MAIN[artifact.main[0]] || ''} +${artifact.main[1]}`}
                style={{
                    width: size, height: size, borderRadius: '50%', flexShrink: 0, position: 'relative',
                    boxShadow: picked ? '0 0 0 3px rgba(255, 214, 102, 0.95), 0 0 12px 3px rgba(255, 190, 60, 0.7)' : 'none',
                    cursor: onClick ? 'pointer' : 'default'
                }}
            >
                {/* 강화 수치는 붙이지 않는다 — 게임의 아티팩트 칸에는 없다(사용자 지시) */}
                <img
                    src={src} alt={label(artifact)} onError={() => setBroken(true)}
                    style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.5))' }}
                />
            </div>
        );
    }
    // 그림이 없는 경우(빈 칸·처음 보는 값)는 예전처럼 그린다
    const color = artifact
        ? (artifact.type === 1 ? ATTR_COLOR[artifact.attribute] : STYLE_COLOR[artifact.unitStyle]) || '#999'
        : '#5a3d24';
    const short = artifact ? (artifact.type === 1 ? ATTR_SHORT[artifact.attribute] : STYLE_SHORT[artifact.unitStyle]) || '?' : '';
    return (
        <div
            role={onClick && artifact ? 'button' : undefined}
            onClick={artifact ? onClick : undefined}
            title={artifact ? `${label(artifact)} · ${ARTIFACT_MAIN[artifact.main[0]] || ''} +${artifact.main[1]}` : (kind === 1 ? '속성 아티팩트 없음' : '유형 아티팩트 없음')}
            style={{
                width: size, height: size, borderRadius: '50%', flexShrink: 0, position: 'relative',
                background: artifact ? 'radial-gradient(circle at 40% 35%, #6a3b22 0%, #3a2216 70%)' : '#2a1a12',
                border: `${Math.max(2, size * 0.06)}px solid ${color}`,
                boxShadow: picked ? `0 0 0 3px rgba(255, 214, 102, 0.95), 0 0 12px 3px rgba(255, 190, 60, 0.7)` : '0 2px 6px rgba(0,0,0,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: onClick && artifact ? 'pointer' : 'default', opacity: artifact ? 1 : 0.6
            }}
        >
            {artifact ? (
                <span style={{ fontSize: size * 0.38, fontWeight: 900, color, textShadow: '0 1px 0 #000' }}>{short}</span>
            ) : (
                <span style={{ fontSize: size * 0.3, color: '#5a3d24' }}>{kind === 1 ? '속' : '유'}</span>
            )}
        </div>
    );
}

const OUTLINE = '-1px 0 #1a0f08, 1px 0 #1a0f08, 0 -1px #1a0f08, 0 1px #1a0f08';
const text = (size: number, color: string, weight = 500): React.CSSProperties => ({ fontSize: size, color, fontWeight: weight, textShadow: OUTLINE });

/** 아티팩트 창 — 룬 창 자리에 그린다 (바깥 판 바탕을 같이 쓴다) */
export default function ArtifactDetail({ artifact, isMobile, onClose }: { artifact: Artifact; isMobile: boolean; onClose?: () => void }) {
    const q = QUALITY[artifact.rank] || QUALITY[5];
    const f = isMobile ? 0.4 : 0.48;
    const color = (artifact.type === 1 ? ATTR_COLOR[artifact.attribute] : STYLE_COLOR[artifact.unitStyle]) || '#999';
    return (
        <div style={{ color: '#f4efe6' }}>
            <div style={{ display: 'grid', gridTemplateColumns: `${44 * f}px 1fr ${44 * f}px`, alignItems: 'center' }}>
                <span />
                <div style={{ textAlign: 'center', whiteSpace: 'nowrap', ...text(Math.max(13, 30 * f), '#f2b23a', 600) }}>
                    {label(artifact)}
                </div>
                <button onClick={onClose} title="닫기" style={{ justifySelf: 'end', width: 40 * f, height: 40 * f, border: 'none', background: 'transparent', color: '#e9c98a', fontSize: 34 * f, fontWeight: 900, lineHeight: 1, cursor: 'pointer', padding: 0 }}>×</button>
            </div>
            <div style={{ display: 'flex', gap: 14 * f, alignItems: 'center', marginTop: 8 * f }}>
                <ArtifactBadge artifact={artifact} kind={artifact.type} size={110 * f} />
                <div style={{ minWidth: 0 }}>
                    <div style={{ whiteSpace: 'nowrap', ...text(Math.max(15, 40 * f), '#fff') }}>{ARTIFACT_MAIN[artifact.main[0]] || '?'} +{artifact.main[1]}</div>
                    <div style={{ marginTop: 4 * f, display: 'inline-block', padding: `${2 * f}px ${14 * f}px`, borderRadius: 8, background: '#5a2c14', border: '2px solid #8a4d22', ...text(Math.max(11, 22 * f), q.color, 600) }}>{q.name}</div>
                </div>
            </div>
            <div style={{ marginTop: 12 * f, display: 'flex', flexDirection: 'column', gap: 4 * f }}>
                {artifact.subs.map(([id, v], i) => (
                    <div key={i} style={{ lineHeight: 1.3, ...text(Math.max(12, 26 * f), i === 0 ? color : '#f4efe6') }}>{artifactEffectText(id, v)}</div>
                ))}
            </div>
        </div>
    );
}
